/**
 * Builder Flow Event Tracking
 * Tracks user journey through event/course creation
 */

import { posthog } from '../posthog';
import type {
  BuilderOpenedProperties,
  BuilderSectionStartedProperties,
  BuilderSectionCompletedProperties,
  BuilderSavedProperties,
  BuilderPublishedProperties,
  BuilderAdvancedFeatureUsedProperties,
  BuilderAbandonedProperties,
  BuilderSection,
} from '../types';

// Store builder session data
interface BuilderSession {
  startTime: number;
  sectionsStarted: Set<BuilderSection>;
  sectionsCompleted: Set<BuilderSection>;
  sectionStartTimes: Map<BuilderSection, number>;
}

const builderSessions = new Map<string, BuilderSession>();

/**
 * Get or create a builder session key
 */
function getSessionKey(contentId?: string, contentType?: string): string {
  return contentId || `new_${contentType}_${Date.now()}`;
}

export const builderEvents = {
  /**
   * Track when builder page is opened
   * Trigger: Event/course builder page loads
   */
  opened: (props: Omit<BuilderOpenedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const sessionKey = getSessionKey(props.content_id, props.content_type);

    // Initialize session tracking
    builderSessions.set(sessionKey, {
      startTime: timestamp,
      sectionsStarted: new Set(),
      sectionsCompleted: new Set(),
      sectionStartTimes: new Map(),
    });

    posthog.capture('builder_opened', {
      ...props,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track when user starts filling a section
   * Trigger: User begins interacting with a section
   */
  sectionStarted: (props: Omit<BuilderSectionStartedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const sessionKey = getSessionKey(props.content_id, props.content_type);
    const session = builderSessions.get(sessionKey);

    if (session) {
      session.sectionsStarted.add(props.section);
      session.sectionStartTimes.set(props.section, Date.now());
    }

    posthog.capture('builder_section_started', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track when a section passes validation
   * Trigger: Section is complete and valid
   */
  sectionCompleted: (props: Omit<BuilderSectionCompletedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const sessionKey = getSessionKey(props.content_id, props.content_type);
    const session = builderSessions.get(sessionKey);

    let timeSpent = props.time_spent_ms;
    if (session) {
      session.sectionsCompleted.add(props.section);
      const sectionStart = session.sectionStartTimes.get(props.section);
      if (sectionStart && !timeSpent) {
        timeSpent = timestamp - sectionStart;
      }
    }

    posthog.capture('builder_section_completed', {
      ...props,
      time_spent_ms: timeSpent,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track first successful save (event/course created)
   * Trigger: Save button clicked and successful
   */
  saved: (props: Omit<BuilderSavedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const sessionKey = getSessionKey(props.content_id, props.content_type);
    const session = builderSessions.get(sessionKey);

    let timeToSave = props.time_to_save_ms;
    if (session && !timeToSave) {
      timeToSave = timestamp - session.startTime;
    }

    posthog.capture('builder_saved', {
      ...props,
      time_to_save_ms: timeToSave,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track when event/course is published
   * Trigger: Publish button clicked and successful
   */
  published: (props: Omit<BuilderPublishedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const sessionKey = getSessionKey(props.content_id, props.content_type);
    const session = builderSessions.get(sessionKey);

    let timeToPublish = props.time_to_publish_ms;
    if (session && !timeToPublish) {
      timeToPublish = timestamp - session.startTime;
    }

    // Clean up session on publish
    builderSessions.delete(sessionKey);

    posthog.capture('builder_published', {
      ...props,
      time_to_publish_ms: timeToPublish,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Track when user accesses advanced features
   * Trigger: User clicks on advanced feature tabs
   */
  advancedFeatureUsed: (props: Omit<BuilderAdvancedFeatureUsedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    posthog.capture('builder_advanced_feature_used', {
      ...props,
      timestamp_ms: Date.now(),
    });
  },

  /**
   * Track builder abandonment
   * Trigger: User leaves without saving (unsaved changes)
   */
  abandoned: (props: Omit<BuilderAbandonedProperties, 'timestamp_ms'>) => {
    if (!posthog.__loaded) return;

    const timestamp = Date.now();
    const sessionKey = getSessionKey(props.content_id, props.content_type);
    const session = builderSessions.get(sessionKey);

    let timeSpent = props.time_spent_ms;
    let sectionsStarted = props.sections_started;
    let sectionsCompleted = props.sections_completed;

    if (session) {
      if (!timeSpent) {
        timeSpent = timestamp - session.startTime;
      }
      if (!sectionsStarted.length) {
        sectionsStarted = Array.from(session.sectionsStarted);
      }
      if (!sectionsCompleted.length) {
        sectionsCompleted = Array.from(session.sectionsCompleted);
      }
    }

    // Clean up session
    builderSessions.delete(sessionKey);

    posthog.capture('builder_abandoned', {
      ...props,
      time_spent_ms: timeSpent,
      sections_started: sectionsStarted,
      sections_completed: sectionsCompleted,
      timestamp_ms: timestamp,
    });
  },

  /**
   * Get current session data for a builder
   */
  getSession: (contentId?: string, contentType?: string): BuilderSession | undefined => {
    const sessionKey = getSessionKey(contentId, contentType);
    return builderSessions.get(sessionKey);
  },

  /**
   * Clear session data
   */
  clearSession: (contentId?: string, contentType?: string) => {
    const sessionKey = getSessionKey(contentId, contentType);
    builderSessions.delete(sessionKey);
  },
};
